/**
 * Cached loader for Stage reference data, separately per service (hrdc / hrdc_event).
 *
 * WHY THIS EXISTS
 *   Stage data is small, rarely-changing reference data that the frontend needs on
 *   virtually every page to interpret application stageIds. Re-fetching it on every
 *   page load — and once per `new HrdcData()`, of which a page can create ~150 — is
 *   wasteful. Hardcoding it in a bundled file (the old src/objects/stages.js) avoided
 *   the fetch but forked the data into a copy that silently drifts from the DB Stage
 *   table, and a single bundled file cannot represent two different DBs at once
 *   (hrdc and hrdc_event have different Stage tables).
 *
 * WHAT THIS DOES INSTEAD (the DB stays the single source of truth)
 *   1. In-memory promise cache, keyed by service -> collapses the ~150
 *      `new HrdcData()` instantiations on a page into ONE fetch per service.
 *   2. localStorage cache, keyed by service      -> survives reloads, so most loads
 *      do ZERO network and read stages instantly from disk.
 *   3. Cheap revalidation                         -> on a throttled interval it checks
 *      a tiny `/version` endpoint and only refetches the full list when the DB Stage
 *      table actually changed. Falls back to a TTL when `/version` is unavailable, so
 *      it keeps working even before that backend endpoint is deployed.
 *
 * Safe to call as often as you like; concurrent / repeat calls share the same work.
 */

const CACHE_VERSION = "1"; // bump if the SHAPE of cached data changes
const LS_PREFIX = `stages:v${CACHE_VERSION}:`;

// Run the cheap `/version` check at most once per this window, per service.
const REVALIDATE_AFTER_MS = 5 * 60 * 1000; // 5 minutes
// If `/version` is unavailable, force a full refetch once the cache is older than this.
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const memoryCache = {}; // serviceKey -> Promise<Array<stage>>
const syncCache = {}; // serviceKey -> Array<stage>, for synchronous access (see getStagesSync)

function readCache(lsKey) {
  try {
    const raw = localStorage.getItem(lsKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data)) return null;
    return parsed; // { data, sig, cachedAt }
  } catch (e) {
    return null;
  }
}

function writeCache(lsKey, payload) {
  try {
    localStorage.setItem(lsKey, JSON.stringify(payload));
  } catch (e) {
    // Storage full / disabled (e.g. private mode) — caching is best-effort.
  }
}

async function fetchStages(apiUrl) {
  const res = await fetch(`${apiUrl}/v1/en/console/stage/get-stages`);
  const json = await res.json();
  if (!json || json.status === false || !Array.isArray(json.data)) {
    throw new Error("Invalid /get-stages response");
  }
  return json.data;
}

/**
 * Cheap signature of the current Stage table, used to detect changes without
 * downloading the whole list. Returns null if the endpoint is unavailable, in
 * which case the caller falls back to a TTL.
 */
async function fetchSignature(apiUrl) {
  try {
    const res = await fetch(`${apiUrl}/v1/en/console/stage/version`);
    if (!res.ok) return null;
    const json = await res.json();
    if (!json || json.status === false || !json.data) return null;
    return `${json.data.count}:${json.data.maxUpdatedAt}`;
  } catch (e) {
    return null;
  }
}

/**
 * Background, non-blocking freshness check for an existing cache entry. Updates
 * localStorage so the NEXT page load gets fresh data; never throws (a network
 * hiccup just leaves the existing good cache in place).
 */
async function revalidate(lsKey, apiUrl, cached) {
  if (Date.now() - cached.cachedAt < REVALIDATE_AFTER_MS) return; // throttled
  try {
    const sig = await fetchSignature(apiUrl);
    if (sig === null) {
      // No `/version` endpoint -> TTL fallback.
      if (Date.now() - cached.cachedAt < TTL_MS) return;
      const data = await fetchStages(apiUrl);
      writeCache(lsKey, { data, sig: null, cachedAt: Date.now() });
      return;
    }
    if (sig === cached.sig) {
      // Unchanged — just reset the throttle timer.
      writeCache(lsKey, { ...cached, cachedAt: Date.now() });
      return;
    }
    // Changed — refresh the full list.
    const data = await fetchStages(apiUrl);
    writeCache(lsKey, { data, sig, cachedAt: Date.now() });
  } catch (e) {
    // Keep the existing good cache.
  }
}

async function resolve(serviceKey, apiUrl) {
  const lsKey = LS_PREFIX + serviceKey;
  const cached = readCache(lsKey);

  if (cached && cached.data.length) {
    syncCache[serviceKey] = cached.data;
    // Serve cached immediately; refresh in the background for next time.
    revalidate(lsKey, apiUrl, cached);
    return cached.data;
  }

  // Cold cache (first visit / cleared / new browser): fetch once, then cache.
  const data = await fetchStages(apiUrl);
  const sig = await fetchSignature(apiUrl);
  syncCache[serviceKey] = data;
  writeCache(lsKey, { data, sig, cachedAt: Date.now() });
  return data;
}

/**
 * Build the lookup maps consumers expect from a raw stage list.
 *
 * NOTE: the map names are legacy/misleading and preserved verbatim so existing
 * consumers keep working:
 *   - stagePriority[nameSlug] -> stage id
 *   - stageSlug[nameSlug]     -> stagePriority number
 *   - stageIndex[id]          -> stagePriority number
 *   - stageSlugToStageName[nameSlug] -> display name
 *   - allStage[STAGE_NAME]    -> stage id  (returned to `allStages` consumers)
 */
export function buildStageMaps(stages) {
  const allStage = [];
  const stagePriority = [];
  const stageSlug = [];
  const stageIndex = [];
  const stageSlugToStageName = [];

  for (let i = 0; i < stages.length; i++) {
    let resultData = stages[i];
    let stageName = resultData.name.toUpperCase().replace(/ /g, "_");
    let stageNameSlug = resultData.nameSlug;

    allStage[stageName] = resultData.id;
    stagePriority[stageNameSlug] = resultData.id;
    stageSlug[stageNameSlug] = resultData.stagePriority;
    stageIndex[resultData.id] = resultData.stagePriority;
    stageSlugToStageName[stageNameSlug] = resultData.name;
  }

  return { allStage, stagePriority, stageSlug, stageIndex, stageSlugToStageName };
}

/**
 * Load the Stage list for a service, using the in-memory + localStorage caches.
 * @param {"hrdc"|"hrdc_event"} serviceKey - cache namespace (the services differ!)
 * @param {string} apiUrl - base API url (already includes "/api")
 * @returns {Promise<Array<object>>}
 */
export function loadStages(serviceKey, apiUrl) {
  if (!memoryCache[serviceKey]) {
    memoryCache[serviceKey] = resolve(serviceKey, apiUrl).catch((err) => {
      // Don't cache a failed promise — let the next call retry.
      delete memoryCache[serviceKey];
      throw err;
    });
  }
  return memoryCache[serviceKey];
}

/**
 * Synchronous, best-effort access to a service's stages — for callers that can't
 * await (e.g. Tabulator formatters, model config). Reads the in-memory cache, then
 * falls back to a synchronous localStorage read. Returns [] if nothing is cached
 * yet (the caller should treat a missing stage as "no match" rather than crash).
 *
 * In practice the data is already cached by the time these run, because the view's
 * `new HrdcData()` kicks off `loadStages` (populating both caches) well before the
 * table/report renders its rows.
 *
 * @param {"hrdc"|"hrdc_event"} serviceKey
 * @returns {Array<object>}
 */
export function getStagesSync(serviceKey) {
  if (syncCache[serviceKey]) return syncCache[serviceKey];
  const cached = readCache(LS_PREFIX + serviceKey);
  if (cached && cached.data.length) {
    syncCache[serviceKey] = cached.data;
    return cached.data;
  }
  return [];
}

/**
 * Resolve a stage id from its nameSlug, synchronously, for a given service.
 * Returns undefined if the slug is unknown or stages aren't cached yet.
 * @param {"hrdc"|"hrdc_event"} serviceKey
 * @param {string} slug
 * @returns {number|undefined}
 */
export function stageIdBySlug(serviceKey, slug) {
  const found = getStagesSync(serviceKey).find((s) => s.nameSlug === slug);
  return found ? found.id : undefined;
}
